import { useEffect, useRef, useState } from 'react';
import { useArchiveBlock } from '@/blocks/application/hooks/useArchiveBlock';
import { useUnarchiveBlock } from '@/blocks/application/hooks/useUnarchiveBlock';
import { useOpenBlockEditor } from '@/blocks/application/hooks/useOpenBlockEditor';
import { useDuplicateBlock } from '@/blocks/application/hooks/useDuplicateBlock';
import { ConflictError } from '@/common/infrastructure/http/problemDetails';
import { isPersistentSeverity } from '@/common/presentation/hooks/useSnackbar';
import { problemDetailOrFallback } from '@/blocks/presentation/utils/userFacingProblemMessage';
import type { Block } from '@/blocks/domain/types';

type SnackbarSeverity = 'success' | 'error';

/**
 * Block Library page controller: dialog/snackbar state, and the archive / duplicate / restore
 * handlers, moved out of `BlockLibraryPage.tsx` so the page stays a thin composition of hooks +
 * `LibraryGallery` (LLAMS-617 Part 3 plan). Filter state and paging are deliberately NOT owned
 * here — they come straight from `useBlockLibraryUrlFilters` / `useInfiniteBlocks`, each its
 * own single source of truth.
 */
export function useBlockLibraryPageController() {
  const openBlock = useOpenBlockEditor();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');
  const [duplicatePendingBlockId, setDuplicatePendingBlockId] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Block | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<Block | null>(null);
  // Counts successful archive/restore operations: the page moves focus to its heading on each, because the
  // card that opened the dialog is gone by then (focus would otherwise fall to <body>).
  const [focusHeadingToken, setFocusHeadingToken] = useState(0);

  const snackbarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showSnackbarMessage = (message: string, severity: SnackbarSeverity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    if (snackbarTimeoutRef.current) {
      window.clearTimeout(snackbarTimeoutRef.current);
      snackbarTimeoutRef.current = null;
    }
    // An error stays until it is dismissed (or replaced); only a success message fades.
    if (isPersistentSeverity(severity)) return;
    snackbarTimeoutRef.current = window.setTimeout(() => setSnackbarMessage(null), 6000);
  };

  useEffect(() => {
    return () => {
      if (snackbarTimeoutRef.current) window.clearTimeout(snackbarTimeoutRef.current);
      snackbarTimeoutRef.current = null;
    };
  }, []);

  const archive = useArchiveBlock();
  const unarchive = useUnarchiveBlock();
  const duplicate = useDuplicateBlock();

  const handleDuplicate = async (block: Block) => {
    setDuplicatePendingBlockId(block.blockId);
    setArchiveError(null);
    try {
      const copy = await duplicate.mutateAsync({ blockId: block.blockId });
      showSnackbarMessage('Block duplicated successfully', 'success');
      openBlock(copy.blockId);
    } catch (err) {
      // Never the raw transport text ('Network Error', 'HTTP 500'): the API's own sentence, or a plain one.
      showSnackbarMessage(problemDetailOrFallback(err, 'Failed to duplicate block'), 'error');
    } finally {
      setDuplicatePendingBlockId(null);
    }
  };

  const openArchiveDialog = (block: Block) => {
    setArchiveError(null);
    setArchiveTarget(block);
  };

  const closeArchiveDialog = () => {
    if (archive.isPending) return;
    setArchiveError(null);
    setArchiveTarget(null);
  };

  const handleConfirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiveError(null);

    try {
      await archive.mutateAsync(archiveTarget.blockId);
      showSnackbarMessage('Block archived successfully', 'success');
      setArchiveTarget(null);
      setFocusHeadingToken((token) => token + 1);
    } catch (err) {
      // ONE channel for a failed archive (BL-07): inline in the confirm dialog. The CRM host's toast is
      // suppressed by the mutation's meta, and no snackbar copy is raised.
      if (err instanceof ConflictError) {
        setArchiveError(
          err.problem?.detail ?? err.message ?? 'This block cannot be archived because it is still referenced by one or more templates.'
        );
      } else {
        setArchiveError(err instanceof Error ? err.message : 'Failed to archive block.');
      }
    }
  };

  const handleConfirmRestore = async () => {
    if (!restoreTarget) return;
    setArchiveError(null);

    try {
      await unarchive.mutateAsync(restoreTarget.blockId);
      showSnackbarMessage('Block restored successfully', 'success');
      setRestoreTarget(null);
      setFocusHeadingToken((token) => token + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to restore block.';
      showSnackbarMessage(message, 'error');
    }
  };

  return {
    openBlock,
    focusHeadingToken,
    dialogOpen,
    setDialogOpen,
    archiveError,
    snackbarMessage,
    snackbarSeverity,
    setSnackbarMessage,
    duplicatePendingBlockId,
    archiveTarget,
    isArchivePending: archive.isPending,
    restoreTarget,
    setRestoreTarget,
    isRestorePending: unarchive.isPending,
    handleDuplicate,
    openArchiveDialog,
    closeArchiveDialog,
    handleConfirmArchive,
    handleConfirmRestore
  };
}
